/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * First-party cookie consent store (PIPEDA meaningful consent).
 *
 * Storage: first-party cookie `sf_cookie_consent` (not localStorage). Path `/`,
 * SameSite=Lax, Secure on HTTPS, 12-month TTL.
 *
 * Categories: `essential` (always true — session, i18n, captcha, UI chrome) and
 * `analytics` (Google Analytics and Sentry Session Replay). Public policy copy
 * for these categories is owned by P10.
 */

import Cookies from 'universal-cookie'

export type CookieConsent = {
  essential: true
  analytics: boolean
}

type StoredConsentPayload = {
  v: 1
  analytics: boolean
}

const COOKIE_NAME = 'sf_cookie_consent'
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000
const DEFAULT_CONSENT: CookieConsent = {
  essential: true,
  analytics: false,
}

const openSettingsListeners = new Set<() => void>()

function createCookies(): Cookies {
  return new Cookies()
}

function parseStoredConsent(raw: unknown): StoredConsentPayload | null {
  let value = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) as unknown
    } catch {
      return null
    }
  }

  if (value === null || typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>
  if (record.v !== 1 || typeof record.analytics !== 'boolean') {
    return null
  }

  return { v: 1, analytics: record.analytics }
}

export function getCookieConsent(): CookieConsent {
  if (typeof document === 'undefined') {
    return DEFAULT_CONSENT
  }

  const stored = parseStoredConsent(createCookies().get(COOKIE_NAME))
  if (!stored) {
    return DEFAULT_CONSENT
  }

  return {
    essential: true,
    analytics: stored.analytics,
  }
}

export function hasStoredConsentChoice(): boolean {
  if (typeof document === 'undefined') {
    return false
  }

  return parseStoredConsent(createCookies().get(COOKIE_NAME)) !== null
}

export function setCookieConsent(consent: { analytics: boolean }): void {
  if (typeof document === 'undefined') {
    return
  }

  const expires = new Date()
  expires.setTime(expires.getTime() + CONSENT_MAX_AGE_MS)

  const payload: StoredConsentPayload = {
    v: 1,
    analytics: consent.analytics,
  }

  createCookies().set(COOKIE_NAME, JSON.stringify(payload), {
    path: '/',
    expires,
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  })
}

export function openCookieSettings(): void {
  for (const listener of openSettingsListeners) {
    listener()
  }
}

export function subscribeOpenCookieSettings(listener: () => void): () => void {
  openSettingsListeners.add(listener)
  return () => {
    openSettingsListeners.delete(listener)
  }
}
